'use client';

import { createContext, useContext, useSyncExternalStore, type ReactNode } from 'react';
import {
  VAT_DISPLAY_KEY,
  fetchTradeStatus,
  readVatDisplay,
  shownBasis,
  writeVatDisplay,
  type VatBasis,
  type VatDisplayState,
} from '@/src/lib/vatDisplay';

/**
 * The storefront's VAT display state, shared by every price on the page and the header switch.
 *
 * <p>Two facts, both kept outside React so every subscriber sees the same answer:
 * <ul>
 *   <li>the chosen basis, from `localStorage` (see `src/lib/vatDisplay.ts`), followed across
 *       tabs through the `storage` event and held in memory when storage is unavailable, so
 *       the switch still works for the page's lifetime in a private window;</li>
 *   <li>whether the shopper is an approved trade customer, asked of the BFF at most once a
 *       minute, however many prices subscribe.</li>
 * </ul>
 *
 * <p>The server render — and the first client render, which must match it — shows inc VAT and
 * no switch: the public view. A trade customer's choice applies once the page is live.
 */

const TRADE_TTL_MS = 60_000;

const listeners = new Set<() => void>();
let memoryChoice: VatBasis | null = null;
let trade = false;
let tradeCheckedAt = 0;
let tradeRequest: Promise<void> | null = null;

function emit() {
  for (const listener of listeners) listener();
}

function onStorage(event: StorageEvent) {
  if (event.key !== null && event.key !== VAT_DISPLAY_KEY) return;
  memoryChoice = null;
  emit();
}

function refreshTrade() {
  if (tradeRequest || Date.now() - tradeCheckedAt < TRADE_TTL_MS) return;
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH ?? '';
  tradeRequest = fetchTradeStatus(`${basePath}/api/storefront/prices`).then((isTrade) => {
    tradeCheckedAt = Date.now();
    tradeRequest = null;
    if (isTrade !== trade) {
      trade = isTrade;
      emit();
    }
  });
}

function subscribe(listener: () => void) {
  if (listeners.size === 0) window.addEventListener('storage', onStorage);
  listeners.add(listener);
  refreshTrade();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) window.removeEventListener('storage', onStorage);
  };
}

const chosenSnapshot = (): VatBasis => memoryChoice ?? readVatDisplay();
const tradeSnapshot = (): boolean => trade;
const publicBasis = (): VatBasis => 'inc';
const publicTrade = (): boolean => false;

/** Stores the choice and tells every price on the page. */
function choose(basis: VatBasis) {
  memoryChoice = basis;
  writeVatDisplay(basis);
  emit();
}

/** {@link VatDisplayState}, plus the way to change it. */
export interface VatDisplay extends VatDisplayState {
  choose: (basis: VatBasis) => void;
}

export function useVatDisplay(): VatDisplay {
  const chosen = useSyncExternalStore(subscribe, chosenSnapshot, publicBasis);
  const isTrade = useSyncExternalStore(subscribe, tradeSnapshot, publicTrade);
  return { trade: isTrade, basis: shownBasis(isTrade, chosen), choose };
}

/** The `price.*` strings, handed down from the server so no catalogue is bundled here. */
const PriceMessagesContext = createContext<Record<string, string>>({});

export function PriceMessagesProvider({
  messages,
  children,
}: {
  messages: Record<string, string> | undefined;
  children: ReactNode;
}) {
  return (
    <PriceMessagesContext.Provider value={messages ?? {}}>{children}</PriceMessagesContext.Provider>
  );
}

/** The header's "inc VAT / ex VAT" switch. Its host renders it only for a trade customer. */
export function VatDisplaySwitch() {
  const messages = useContext(PriceMessagesContext);
  const { basis, choose } = useVatDisplay();
  const text = (key: string) => messages[key] ?? key;
  const label = text('price.basis.label');

  return (
    <div role="group" aria-label={label} className="flex items-center justify-end gap-2 text-sm">
      <span className="text-[var(--color-text-secondary)]">{label}</span>
      {(['inc', 'ex'] as const).map((option) => (
        <button
          key={option}
          type="button"
          aria-pressed={basis === option}
          onClick={() => choose(option)}
          className={
            basis === option
              ? 'rounded-full bg-[var(--color-brand-600)] px-3 py-1 font-medium text-[var(--color-text-on-brand)]'
              : 'rounded-full border border-[var(--color-border-subtle)] px-3 py-1 text-[var(--color-text-secondary)] hover:border-[var(--color-brand-500)]'
          }
        >
          {text(option === 'inc' ? 'price.taxIncluded' : 'price.taxExcluded')}
        </button>
      ))}
    </div>
  );
}
